import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBell, FiChevronRight, FiX } from 'react-icons/fi';
import { API_BASE_URL, getAuthHeaders } from '../utils/utils';
import { getStoredUser } from '../utils/authSession';
import { notificationPath } from '../utils/notificationRoute';
import './NotificationInbox.css';

const formatTimeAgo = (value) => {
  if (!value) {
    return '';
  }
  const created = new Date(value);
  if (Number.isNaN(created.getTime())) {
    return '';
  }
  const delta = Date.now() - created.getTime();
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const badgeLabel = (unread) => (unread > 99 ? '99+' : String(unread));

const NotificationInbox = ({ variant = 'header', onNavigate }) => {
  const navigate = useNavigate();
  const user = getStoredUser();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const isSheet = variant === 'sheet';

  const load = useCallback(async () => {
    if (!user?.email) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/notifications/?userEmail=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      const data = await response.json();
      setNotifications(Array.isArray(data) ? data : data.notifications || []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    load();
  }, [load]);

  const unread = notifications.filter((row) => !row.is_read).length;

  const openPanel = () => {
    setOpen(true);
    load();
  };

  const closePanel = () => {
    setOpen(false);
  };

  const handleOpen = (notification) => {
    const path = notificationPath(notification);
    if (notification?.id && !notification.is_read) {
      setNotifications((current) =>
        current.map((row) =>
          row.id === notification.id ? { ...row, is_read: true } : row
        )
      );
      fetch(`${API_BASE_URL}/api/notifications/${notification.id}/read`, {
        method: 'POST',
        headers: getAuthHeaders(),
      }).catch(() => {
        // Keep the panel usable even if mark-read fails.
      });
    }
    if (path) {
      closePanel();
      onNavigate?.(path);
      navigate(path);
    }
  };

  return (
    <div className={`ni ni-${variant}${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className={
          isSheet
            ? 'liwaza-more-sheet__item ni-trigger ni-trigger-sheet'
            : `ni-trigger ni-trigger-${variant}`
        }
        onClick={open ? closePanel : openPanel}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        {isSheet ? (
          <>
            <span className="liwaza-more-sheet__item-icon">
              <FiBell size={20} />
            </span>
            <span className="liwaza-more-sheet__item-label">
              Notifications
              {unread > 0 ? (
                <span className="ni-badge">{badgeLabel(unread)}</span>
              ) : null}
            </span>
          </>
        ) : (
          <FiBell size={20} />
        )}
        {!isSheet && unread > 0 ? (
          <span className="ni-badge">{badgeLabel(unread)}</span>
        ) : null}
      </button>
      {open ? (
        <div
          className="ni-overlay"
          onClick={isSheet ? undefined : closePanel}
        >
          <div
            className="ni-panel"
            role="dialog"
            aria-label="Notifications"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="ni-header">
              {isSheet ? (
                <button
                  type="button"
                  className="ni-header-btn"
                  onClick={closePanel}
                  aria-label="Back to menu"
                >
                  <FiArrowLeft size={18} />
                </button>
              ) : null}
              <h2>Notifications</h2>
              {isSheet ? (
                <span className="ni-header-spacer" />
              ) : (
                <button
                  type="button"
                  className="ni-header-btn"
                  onClick={closePanel}
                  aria-label="Close notifications"
                >
                  <FiX size={16} />
                </button>
              )}
            </header>
            <div className="ni-list">
              {loading ? (
                <p className="ni-status">Loading notifications…</p>
              ) : notifications.length ? (
                notifications.map((notification) => {
                  const path = notificationPath(notification);
                  return (
                    <button
                      key={notification.id}
                      type="button"
                      className={`ni-item${path ? ' is-link' : ''}${
                        notification.is_read ? '' : ' is-unread'
                      }`}
                      onClick={() => handleOpen(notification)}
                    >
                      {!notification.is_read ? <i className="ni-dot" /> : <i className="ni-dot is-read" />}
                      <div className="ni-item-body">
                        <div className="ni-item-top">
                          <strong>{notification.title || 'Notification'}</strong>
                          <span>{formatTimeAgo(notification.created_at)}</span>
                        </div>
                        {notification.message ? <p>{notification.message}</p> : null}
                      </div>
                      {path ? <FiChevronRight className="ni-chevron" size={16} /> : null}
                    </button>
                  );
                })
              ) : (
                <div className="ni-empty">
                  <FiBell size={28} />
                  <strong>You're all caught up</strong>
                  <p>New comments and reviews will show up here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationInbox;
